import * as React from 'react';

export const COMPARISON_SCROLL_REGION_LABEL = 'Comparison columns';

export type ComparisonScrollRegionPart = 'header' | 'body';

export function comparisonScrollRegionLabel(
  base: string,
  part: ComparisonScrollRegionPart,
): string {
  return `${base} (${part})`;
}

export const FOCUSABLE_SCROLL_REGION_CLASSNAME =
  'rounded-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700 focus-visible:ring-offset-2';

const OVERFLOW_THRESHOLD_PX = 1;
const MIN_ARROW_SCROLL_PX = 80;

export const COMPARISON_SCROLL_REGION_NAMES = [
  'desktop-header',
  'desktop-body',
  'mobile-header',
  'mobile-body',
] as const;

export function isHorizontallyOverflowing(el: HTMLElement): boolean {
  return el.scrollWidth - el.clientWidth > OVERFLOW_THRESHOLD_PX;
}

/**
 * Arrow keys move one comparison column. Desktop colgroups start with a
 * sticky label col; mobile cols are all data columns.
 */
export function resolveArrowScrollDelta(el: HTMLElement): number {
  const cols = el.querySelectorAll('colgroup col');
  const dataCol = (cols.length > 1 ? cols[1] : cols[0]) as
    | HTMLElement
    | undefined;
  const colWidth = dataCol?.getBoundingClientRect().width ?? 0;
  if (colWidth > 1) return Math.round(colWidth);
  return Math.max(MIN_ARROW_SCROLL_PX, Math.round(el.clientWidth / 3));
}

export function handleScrollRegionKeyDown(
  event: React.KeyboardEvent<HTMLElement>,
): void {
  if (event.target !== event.currentTarget) return;

  const el = event.currentTarget;
  const page = el.clientWidth;
  if (page <= 0) return;
  const column = resolveArrowScrollDelta(el);

  switch (event.key) {
    case 'ArrowLeft':
      event.preventDefault();
      el.scrollBy({ left: -column, behavior: 'smooth' });
      break;
    case 'ArrowRight':
      event.preventDefault();
      el.scrollBy({ left: column, behavior: 'smooth' });
      break;
    case 'Home':
      event.preventDefault();
      el.scrollTo({ left: 0, behavior: 'smooth' });
      break;
    case 'End':
      event.preventDefault();
      el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' });
      break;
    case 'PageUp':
      event.preventDefault();
      el.scrollBy({ left: -page, behavior: 'smooth' });
      break;
    case 'PageDown':
      event.preventDefault();
      el.scrollBy({ left: page, behavior: 'smooth' });
      break;
    default:
      break;
  }
}

export interface ScrollRegionFocusProps {
  tabIndex?: number;
  role?: 'group';
  'aria-label'?: string;
  onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void;
}

/**
 * Makes a horizontal overflow container a keyboard tab stop only while it
 * actually overflows, and wires standard scrolling keys.
 *
 * `contentKey` should change when inner content width can change without the
 * container resizing (e.g. column count). The effect also observes child
 * elements so table `minWidth` updates recompute overflow.
 */
export function useScrollRegionFocus(
  ref: React.RefObject<HTMLElement | null>,
  ariaLabel: string = COMPARISON_SCROLL_REGION_LABEL,
  contentKey: unknown = 0,
): { isOverflowing: boolean; focusProps: ScrollRegionFocusProps } {
  const [isOverflowing, setIsOverflowing] = React.useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const update = () => {
      setIsOverflowing(isHorizontallyOverflowing(el));
    };

    const observer = new ResizeObserver(update);
    const observeTree = () => {
      observer.disconnect();
      observer.observe(el);
      Array.from(el.children).forEach((child) => {
        observer.observe(child);
      });
    };

    observeTree();
    update();

    const mutationObserver = new MutationObserver(observeTree);
    mutationObserver.observe(el, { childList: true });
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [ref, contentKey]);

  const focusProps = React.useMemo<ScrollRegionFocusProps>(() => {
    if (!isOverflowing) return {};
    return {
      tabIndex: 0,
      role: 'group',
      'aria-label': ariaLabel,
      onKeyDown: handleScrollRegionKeyDown,
    };
  }, [ariaLabel, isOverflowing]);

  return { isOverflowing, focusProps };
}
